import React, {  useCallback, useEffect, useMemo, useState , useRef } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RoomIcon from "@mui/icons-material/Room";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, isSameDay } from "date-fns";
import DashboardLayout from "../../layouts/DashboardLayout";
import ShipmentsMap, {
  MapShipment,
} from "../../components/ShipmentsMap/ShipmentsMap";
import {
  darkMapStyle,
  lightMapStyle,
} from "../../components/ShipmentsMap/mapStyles";
import {
  getAddressCandidates,
  useShipmentGeocodes,
} from "../../utils/geocode";
import { Shipment, ShipmentStatus } from "../../types/DB2";
import { useShipmentManager } from "../../context/ShipmentManagerProvider";
import { useThemes } from "../../context/ThemesProvider";
import { formatStatusLabel } from "../../utils/status";

const ACTIVE_STATUS_SET = new Set<ShipmentStatus>([
  "created",
  "dispatched",
  "inTransit",
  "arrived",
  "delayed",
  "completed",
]);

const STATUS_LABELS: Record<ShipmentStatus, string> = {
  created: "Created",
  dispatched: "Dispatched",
  inTransit: "In Transit",
  arrived: "Arrived",
  completed: "Completed",
  delayed: "Delayed",
};

const getShipmentDate = (shipment: Shipment): Date | null => {
  if (!shipment.shipmentDate) return null;
  if (typeof (shipment.shipmentDate as any).toDate === "function") {
    return (shipment.shipmentDate as any).toDate();
  }
  try {
    return new Date(shipment.shipmentDate as unknown as string | number);
  } catch {
    return null;
  }
};

const shipmentHasOrders = (shipment: Shipment): boolean => {
  const snapshot = shipment?.ordersSnapshot;
  if (!snapshot) return false;
  return Object.keys(snapshot).some((key) => {
    const order = snapshot[key];
    return !!order && typeof order === "object";
  });
};

const Home: React.FC = () => {
  const { shipments, refreshShipments } = useShipmentManager();
  const { themeVariant } = useThemes();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(
    null
  );
  const [refreshing, setRefreshing] = useState(false);
  const hasAutoSelectedRef = useRef(false);

  const normalizedSelected = useMemo(
    () => selectedDate.startOf("day"),
    [selectedDate]
  );
  const today = useMemo(() => dayjs().startOf("day"), []);
  const viewingPast = normalizedSelected.isBefore(today, "day");

  const shipmentsForDay = useMemo(() => {
    return shipments.filter((shipment) => {
      const date = getShipmentDate(shipment);
      if (!date) return false;
      return isSameDay(date, normalizedSelected.toDate());
    });
  }, [shipments, normalizedSelected]);

  const filteredShipments = useMemo(() => {
    if (!shipmentsForDay.length) {
      return [];
    }
    if (viewingPast) {
      return shipmentsForDay;
    }
    return shipmentsForDay.filter((shipment) =>
      ACTIVE_STATUS_SET.has(shipment.status)
    );
  }, [shipmentsForDay, viewingPast]);

  useEffect(() => {
    const dayLabel = normalizedSelected.format("YYYY-MM-DD");
    const filteredIds = new Set(filteredShipments.map((shipment) => shipment.id));
    const excluded = shipmentsForDay
      .filter((shipment) => !filteredIds.has(shipment.id))
      .map(({ id, status }) => ({ id, status }));
    console.debug("[AdminHome] shipmentsForDay summary", {
      day: dayLabel,
      viewingPast,
      total: shipmentsForDay.length,
      statuses: shipmentsForDay.map(({ id, status }) => ({ id, status })),
      includedIds: Array.from(filteredIds),
      filteredOut: excluded,
    });
  }, [filteredShipments, shipmentsForDay, normalizedSelected, viewingPast]);

  useEffect(() => {
    try {
      const dayLabel = normalizedSelected.format("YYYY-MM-DD");
      const shipmentSummaries = shipments.map((shipment) => {
        const date = getShipmentDate(shipment);
        return {
          id: shipment.id,
          status: shipment.status,
          hasOrdersSnapshot: !!shipment.ordersSnapshot && Object.keys(shipment.ordersSnapshot).length > 0,
          dateIso: date ? dayjs(date).format("YYYY-MM-DD") : null,
          rawDate: shipment.shipmentDate,
        };
      });
      console.debug("[AdminHome] render snapshot", {
        selectedDay: dayLabel,
        totalShipments: shipments.length,
        shipmentsForDay: shipmentsForDay.length,
        filteredCount: filteredShipments.length,
        viewingPast,
        shipmentSummaries,
      });
    } catch (error) {
      console.warn("[AdminHome] render snapshot logging failed", error);
    }
  }, [shipments, shipmentsForDay, filteredShipments, normalizedSelected, viewingPast]);

  const { locations, pending: geoPending } = useShipmentGeocodes({
    shipments: filteredShipments,
  });

  const shipmentsMissingLocation = useMemo(
    () =>
      filteredShipments.filter((shipment) => !locations[shipment.id]),
    [filteredShipments, locations]
  );

  const summary = useMemo(() => {
    const total = filteredShipments.length;
    const withLocation = filteredShipments.filter(
      (shipment) => locations[shipment.id]
    ).length;
    return {
      total,
      withLocation,
      withoutLocation: total - withLocation,
    };
  }, [filteredShipments, locations]);

  const mapShipments: MapShipment[] = useMemo(() => {
    return filteredShipments.map((shipment) => {
      const date = getShipmentDate(shipment);
      const orderEntries = shipment.ordersSnapshot
        ? Object.entries(shipment.ordersSnapshot)
        : [];

      const orderSummaries = orderEntries.map(([orderId, orderSnapshot]) => {
        const itemSnapshots = orderSnapshot?.includedItemsSnapshot
          ? Object.values(orderSnapshot.includedItemsSnapshot)
          : [];
        const destinationSet = new Set<string>();
        itemSnapshots.forEach((item) => {
          const dest =
            typeof item?.destination === "string"
              ? item.destination.trim()
              : "";
          if (dest) destinationSet.add(dest);
        });
        if (!destinationSet.size && orderSnapshot?.destinations?.length) {
          orderSnapshot.destinations.forEach((dest) => {
            if (typeof dest === "string" && dest.trim()) {
              destinationSet.add(dest.trim());
            }
          });
        }
        return {
          orderId,
          itemCount: itemSnapshots.length,
          destinations: Array.from(destinationSet),
        };
      });

      const totalItems = orderSummaries.reduce(
        (sum, summary) => sum + summary.itemCount,
        0
      );

      const uniqueDestinations = new Set<string>();
      orderSummaries.forEach((summary) => {
        summary.destinations.forEach((dest) => uniqueDestinations.add(dest));
      });
      if (shipment.destinations?.length) {
        shipment.destinations.forEach((dest) => {
          if (typeof dest === "string" && dest.trim()) {
            uniqueDestinations.add(dest.trim());
          }
        });
      }

      const location = locations[shipment.id];
      const addressCandidates = getAddressCandidates(shipment);
      const primaryAddress =
        location?.formattedAddress ||
        addressCandidates.find((candidate) => candidate.trim()) ||
        undefined;

      if (import.meta.env.DEV) {
        console.log("[AdminHome] shipment order summary", {
          shipmentId: shipment.id,
          orders: orderSummaries.length,
          totalItems,
          uniqueDestinations: uniqueDestinations.size,
          usedAddress: primaryAddress,
        });
      }

      return {
        id: shipment.id,
        status: shipment.status,
        label: shipment.id || "Shipment",
        lat: location?.lat,
        lng: location?.lng,
        subtitle: date ? format(date, "PP") : undefined,
        detailLines: [
          `Status: ${
            STATUS_LABELS[shipment.status] ||
            formatStatusLabel(shipment.status) ||
            shipment.status
          }`,
          `Orders: ${orderSummaries.length}`,
          `Items: ${totalItems}`,
          `Unique destinations: ${
            uniqueDestinations.size ||
            addressCandidates.filter((candidate) => candidate.trim()).length
          }`,
          shipment.drivers?.length
            ? `Drivers: ${shipment.drivers.join(", ")}`
            : undefined,
        ].filter(Boolean) as string[],
        orders: orderSummaries,
      };
    });
  }, [filteredShipments, locations]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refreshShipments();
    } finally {
      setRefreshing(false);
    }
  }, [refreshShipments]);

  const handleSelectDate = (value: Dayjs | null) => {
    if (!value) return;
    const normalized = value.startOf("day");
    setSelectedDate(normalized);
    setSelectedShipmentId(null);
  };

  const handleSelectShipment = (shipmentId: string | null) => {
    setSelectedShipmentId(shipmentId);
  };

  useEffect(() => {
    console.log("[AdminHome] theme selection", {
      themeMode: themeVariant.mode,
    });
  }, [themeVariant.mode]);

  const activeMapStyles = useMemo(
    () => (themeVariant.mode === "dark" ? darkMapStyle : lightMapStyle),
    [themeVariant.mode]
  );

  useEffect(() => {
    console.log("[AdminHome] active map styles", {
      theme: themeVariant.mode,
      stylesLength: activeMapStyles.length,
    });
  }, [activeMapStyles, themeVariant.mode]);

  // On initial load: if today has no shipments, auto-select the most recent shipment date.
  useEffect(() => {
    const todayIso = today.format('YYYY-MM-DD');
    const currentIso = normalizedSelected.format('YYYY-MM-DD');
    console.info('[AdminHome] auto-select tick', {
      hasAutoSelected: hasAutoSelectedRef.current,
      shipmentsCount: shipments?.length ?? 0,
      todayIso,
      currentIso,
    });
    if (hasAutoSelectedRef.current) {
      console.info('[AdminHome] auto-select skip: already selected once');
      return;
    }
    if (!shipments?.length) {
      console.info('[AdminHome] auto-select skip: no shipments loaded yet');
      return;
    }
    const shipmentsWithOrders = shipments.filter((shipment) => shipmentHasOrders(shipment));
    if (!shipmentsWithOrders.length) {
      console.info('[AdminHome] auto-select skip: no shipments contain orders yet');
      return;
    }
    const uniqueValues = Array.from(
      new Set(
        shipmentsWithOrders
          .map((s) => getShipmentDate(s))
          .filter((d): d is Date => !!d)
          .map((d) => dayjs(d).startOf('day').valueOf())
      )
    ).sort((a, b) => a - b);
    const uniqueIso = uniqueValues.map((v) => dayjs(v).format('YYYY-MM-DD'));
    console.info('[AdminHome] auto-select candidate days', {
      uniqueCount: uniqueValues.length,
      uniqueIso,
      eligibleShipments: shipmentsWithOrders.length,
      totalShipments: shipments.length,
    });
    if (!uniqueValues.length) {
      console.info('[AdminHome] auto-select skip: no valid shipment dates');
      return;
    }
    const todayValue = today.valueOf();
    const onOrBefore = uniqueValues.filter((v) => v <= todayValue);
    const targetValue = onOrBefore.length
      ? Math.max(...onOrBefore)
      : Math.min(...uniqueValues);
    const targetIso = dayjs(targetValue).format('YYYY-MM-DD');
    console.info('[AdminHome] auto-select choosing date', {
      targetValue,
      targetIso,
      onOrBeforeCount: onOrBefore.length,
    });
    if (typeof targetValue === 'number' && targetValue !== normalizedSelected.valueOf()) {
      setSelectedDate(dayjs(targetValue));
      console.info('[AdminHome] auto-select applied', { newSelected: targetIso });
    } else {
      console.info('[AdminHome] auto-select no change needed');
    }
    hasAutoSelectedRef.current = true;
  }, [shipments, normalizedSelected, today]);

  return (
    <DashboardLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "primary.main",
              }}
            >
              <LocalShippingIcon
                sx={{ fontSize: "2.5rem", position: "relative", top: -2 }}
                color="primary"
              />
              Shipment Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viewingPast
                ? `Completed deliveries for ${normalizedSelected.format("MMM D, YYYY")}`
                : `Outstanding deliveries for ${normalizedSelected.format("MMM D, YYYY")}`}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label={`Total: ${summary.total}`} size="small" color="primary" />
              <Chip
                label={`Mapped: ${summary.withLocation}`}
                size="small"
                color="success"
                variant="outlined"
              />
              <Chip
                label={`Missing location: ${summary.withoutLocation}`}
                size="small"
                color="warning"
              />
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <DatePicker
              label="Select date"
              value={selectedDate}
              onChange={handleSelectDate}
              format="MMM D, YYYY"
              slots={{
                openPickerIcon: CalendarTodayIcon,
              }}
            />
            <Tooltip title="Jump to today">
              <span>
                <Button
                  variant="outlined"
                  onClick={() => handleSelectDate(dayjs())}
                  disabled={normalizedSelected.isSame(today, "day")}
                >
                  Today
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Refresh shipments">
              <span>
                <IconButton onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? <CircularProgress size={22} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={3}
          sx={{ minHeight: { xs: 560, lg: 520 } }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 360, lg: "100%" },
              borderRadius: 2,
              overflow: "hidden",
              border: 1,
              borderColor: "divider",
            }}
          >
            <ShipmentsMap
              shipments={mapShipments}
              selectedShipmentId={selectedShipmentId}
              onSelect={handleSelectShipment}
              isLoading={geoPending}
              mapStyles={activeMapStyles}
              initialZoom={14}
            />
          </Box>

          <Box
            sx={{
              width: { xs: "100%", lg: 360 },
              maxHeight: { xs: 320, lg: "100%" },
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Shipments
              </Typography>
              <Chip
                label={viewingPast ? "Completed" : "Outstanding"}
                size="small"
                color={viewingPast ? "success" : "warning"}
                variant="outlined"
              />
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {filteredShipments.length === 0 && !geoPending ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 6,
                    px: 2,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2">
                    No shipments match the selected filters.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {mapShipments.map((shipment, index) => {
                    const isMissing = !locations[shipment.id];
                    return (
                      <React.Fragment key={shipment.id}>
                        <ListItemButton
                          selected={selectedShipmentId === shipment.id}
                          onClick={() =>
                            handleSelectShipment(
                              selectedShipmentId === shipment.id ? null : shipment.id
                            )
                          }
                          alignItems="flex-start"
                          sx={{
                            flexDirection: "column",
                            alignItems: "stretch",
                            gap: 0.75,
                            py: 1.5,
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {shipment.label}
                            </Typography>
                            <Chip
                              label={
                                STATUS_LABELS[shipment.status] ||
                                formatStatusLabel(shipment.status) ||
                                shipment.status
                              }
                              size="small"
                              color={
                                shipment.status === "completed"
                                  ? "success"
                                  : shipment.status === "delayed"
                                  ? "error"
                                  : "default"
                              }
                              variant={
                                shipment.status === "delayed" ? "filled" : "outlined"
                              }
                            />
                          </Stack>

                          <Stack direction="row" spacing={1} alignItems="center">
                            <RoomIcon fontSize="small" />
                            <ListItemText
                              primary={
                                shipment.subtitle ? (
                                  <Typography variant="body2" color="text.secondary">
                                    {shipment.subtitle}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2">
                                    {shipment.label}
                                  </Typography>
                                )
                              }
                            />
                            {isMissing && (
                              <Tooltip title="No coordinates available yet">
                                <Badge color="warning" variant="dot" />
                              </Tooltip>
                            )}
                          </Stack>

                          {shipment.detailLines?.length ? (
                            <Typography variant="caption" color="text.secondary">
                              {shipment.detailLines.join(" | ")}
                            </Typography>
                          ) : null}

                          {shipment.orders?.length ? (
                            <Box
                              sx={{
                                mt: 1,
                                pl: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                borderLeft: (theme) =>
                                  `2px solid ${theme.palette.divider}`,
                              }}
                            >
                              {shipment.orders.map((order) => (
                                <Box key={order.orderId}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Order {order.orderId}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Items: {order.itemCount}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Destinations:{" "}
                                    {order.destinations.length
                                      ? order.destinations.join(", ")
                                      : "No item destinations"}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : null}
                        </ListItemButton>
                        {index < mapShipments.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
            {shipmentsMissingLocation.length > 0 && (
              <Box
                sx={{
                  borderTop: 1,
                  borderColor: "divider",
                  px: 2,
                  py: 1.5,
                  backgroundColor: "background.default",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {shipmentsMissingLocation.length} shipment
                  {shipmentsMissingLocation.length > 1 ? "s" : ""} without map
                  coordinates. Address geocoding runs automatically when data is
                  available.
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </DashboardLayout>
  );
};

export default Home;









